import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, OnDestroy, viewChild } from '@angular/core';
import { RouterLink } from '@angular/router';
import { UserService } from '@shared';
import { UIIconComponent, UiButtonComponent } from '@design-system';
import { LogoutBottomSheetComponent } from './components/logout-bottom-sheet/logout-bottom-sheet.component';
import { SocialLinksBottomSheetComponent } from './components/social-links-bottom-sheet/social-links-bottom-sheet.component';
import { SupportBottomSheetComponent } from './components/support-bottom-sheet/support-bottom-sheet.component';
import { MenuHeaderEventProducerService } from './events/menu-header-event-producer.service';
import { MENU_ACCOUNT_ROUTE, MENU_LIST_ITEMS, MENU_TILES, MenuListItem } from './menu.model';
import { RocketService } from '@third-party';
import { AppUpdateService } from '../../services/app-update.service';
import { LayoutService } from '../../services/layout.service';

/**
 * The سایر page — the single way in to everything the bottom navigation has no
 * room for: the account, the quick-access features, and the app-wide settings.
 *
 * It carries its own profile card at the top, so it takes the app bar down for
 * as long as it is on screen and puts it back on the way out.
 */
@Component({
	selector: 'app-menu',
	imports: [
		NgTemplateOutlet,
		RouterLink,
		UIIconComponent,
		UiButtonComponent,
		LogoutBottomSheetComponent,
		SocialLinksBottomSheetComponent,
		SupportBottomSheetComponent
	],
	templateUrl: './menu.component.html',
	providers: [MenuHeaderEventProducerService],
	host: {
		class: 'block h-full'
	}
})
export class MenuComponent implements OnDestroy {
	private readonly userService = inject(UserService);
	private readonly headerProducer = inject(MenuHeaderEventProducerService);
	private readonly appUpdate = inject(AppUpdateService);
	private rocketService = inject(RocketService);

	private readonly logoutSheet = viewChild<LogoutBottomSheetComponent>('logoutSheet');
	private readonly socialSheet = viewChild<SocialLinksBottomSheetComponent>('socialSheet');
	private readonly supportSheet = viewChild<SupportBottomSheetComponent>('supportSheet');

	readonly checkingForUpdate = this.appUpdate.checkingForUpdate;
	readonly versionLabel = this.appUpdate.versionLabel;

	readonly isDesktop = inject(LayoutService).isDesktop;
	readonly tiles = MENU_TILES;
	readonly listItems = MENU_LIST_ITEMS;
	readonly accountRoute = MENU_ACCOUNT_ROUTE;

	constructor() {
		this.rocketService.subscribeToLoadRocket();
		this.headerProducer.hideHeader();
	}

	ngOnDestroy(): void {
		this.headerProducer.resetHeader();
	}

	/** The signed-in trader's display name, as the access token spells it. */
	readonly userName = computed(() => this.userService.userAccountInfoSignal()?.userFullName?.trim() || '');

	onListItemClick(item: MenuListItem): void {
		if (item.action === 'social') {
			this.socialSheet()?.open();
		} else if (item.action === 'support') {
			this.supportSheet()?.open();
		}
	}

	rocket() {
		this.rocketService.rocket();
	}

	onLogoutClick(): void {
		this.logoutSheet()?.open();
	}

	onGetLatestVersion(): void {
		this.appUpdate.reloadToLatest().subscribe();
	}
}
